import dayjs from 'dayjs'
import { dayjsLocalizer, Views } from 'react-big-calendar'
import { OrderStatus } from '../types/imaging.type'
import type { ImagingOrderStatus } from '../types/imaging_order.type'
import { MARStatus } from '../types/mar.type'
import { statusType } from '../types/medication.type'
import { createOption } from '../utils'

export const CHIEF_COMPLAINT_MAX_LENGTH = 1000

const { ongoing, completed, discontinued, onhold, review_requested, verified, pending } = statusType

export const medicationStatus = [ongoing, completed, onhold, discontinued, review_requested, verified, pending]

export type PatientDemographicItem = {
  label: string
  value: string
  status?: 'success'
}

export type PatientDemographicSection = {
  title: string
  items: PatientDemographicItem[]
}
// export const options = [
//   {
//     handleClick: () => {},
//     label: 'English'
//   },
//   {
//     handleClick: () => {},
//     label: 'French'
//   },
//   {
//     handleClick: () => {},
//     label: 'Dutch'
//   }
// ]

export const roomType = ['Inpatient Room', 'Private Room', 'Semi-private Room', 'Recovery Room']

export type IDataOneType = {
  id: string
  name: string
  role: string
  department: string
  email: string
  status: 'active'
  actions: number
}

export const dosageFormOptions = [
  'tablets',
  'capsules',
  'suppositories',
  'pessaries',
  'ointments',
  'metered dose inhalers',
  'eye drops',
  'creams',
  'caplets',
  'emulsions',
  'gels',
  'syrups',
  'patches',
  'suspensions'
]

export const drugRouteOptions = [
  'oral',
  'inhalation',
  'intravenous',
  'intramuscular',
  'intraosseous',
  'intraperitoneal',
  'intrathecal',
  'subcutaneous',
  'epidural',
  'rectal',
  'topical',
  'transdermal'
]

export const optionRelationship = [
  'Mother',
  'Father',
  'Spouse',
  'Brother',
  'Sister',
  'Son',
  'Daughter',
  'Grandfather',
  'Cousin',
  'Niece or Nephew'
]

export const genderOptions = [
  { name: '', value: '' },
  { name: 'Male', value: 'male' },
  { name: 'Female', value: 'female' }
]

export const patientStatus = [
  { name: '', value: '' },
  { name: 'Living', value: 'living' },
  { name: 'Deceased', value: 'deceased' }
]

export const patientTypeOptions = [
  { name: 'Inpatient', value: 'inpatient' },
  { name: 'Outpatient', value: 'outpatient' }
]

export const orderStatusOptions = [
  { name: 'Pending', value: OrderStatus.pending },
  { name: 'Completed', value: OrderStatus.completed },
  { name: 'Cancelled', value: OrderStatus.cancelled }
]

/**
 * Imaging order bundles also sit in `in_progress` (a result is uploaded but the order
 * isn't complete). Kept separate from `orderStatusOptions`, which lab shares.
 */
export const imagingOrderStatusOptions: { name: string; value: ImagingOrderStatus }[] = [
  { name: 'Pending', value: OrderStatus.pending },
  { name: 'In Progress', value: OrderStatus.in_progress },
  { name: 'Completed', value: OrderStatus.completed },
  { name: 'Cancelled', value: OrderStatus.cancelled }
]

export const procedureOrderStatusOptions = [
  { name: 'Pending', value: OrderStatus.pending },
  { name: 'Signed', value: OrderStatus.signed },
  { name: 'Cancelled', value: OrderStatus.cancelled },
  { name: 'Scheduled', value: OrderStatus.scheduled },
  { name: 'Completed', value: OrderStatus.completed }
]

export const marStatusOptions = [
  { name: 'Administered', value: MARStatus.ADMINISTERED },
  { name: 'Wasted', value: MARStatus.WASTED },
  { name: 'Refused', value: MARStatus.REFUSED },
  { name: 'Late Administered', value: MARStatus.LATE_ADMINISTERED }
]
export const selectableMarStatusOptions = marStatusOptions.filter(item => item.value !== MARStatus.LATE_ADMINISTERED)
export const lateAdministeredArray = marStatusOptions.filter(item => item.value === MARStatus.LATE_ADMINISTERED)
export const relationOptions = [
  { name: 'Father', value: 'father' },
  { name: 'Mother', value: 'mother' },
  { name: 'Sibling', value: 'sibling' },
  { name: 'Spouse', value: 'spouse' },
  { name: 'Offspring', value: 'offspring' },
  { name: 'Uncle', value: 'Uncle' },
  { name: 'Aunty', value: 'Aunty' },
  { name: 'Grandparent', value: 'grandparent' }
]

export const maritalOption = [
  { name: 'Single', value: 'single' },
  { name: 'Married', value: 'married' },
  { name: 'Divorced', value: 'divorced' },
  { name: 'Widowed', value: 'widowed' }
]

export const allergyOptions = [
  { name: 'Ongoing', value: 'ongoing' },
  { name: 'Completed', value: 'completed' },
  { name: 'discontinued', value: 'discontinued' }
]

export const TableData = [
  {
    date: '2018-01-09',
    location: 'Winston Salem',
    name: 'Alan Josiah Werner Shirleen Foy',
    paid: 3425,
    percent: 24
  },
  {
    date: '2018-06-10',
    location: 'Fort Collins',
    name: 'Bryan Lane Smallwood Dion Gunderson',
    paid: 1234,
    percent: 30
  },
  {
    date: '2018-06-09',
    location: 'Palo Alto',
    name: 'Chris Willa Koehler Rocco Bales',
    paid: 2345,
    percent: 40
  },
  {
    date: '2018-06-11',
    location: 'Palo Alto',
    name: 'Eric Maegan Regalado Kiana Lawton',
    paid: 3456,
    percent: 80
  },
  {
    date: '2018-06-10',
    location: 'Fort Collins',
    name: 'Doug Yong Cleveland Jule Gantt',
    paid: 1234,
    percent: 60
  },
  {
    date: '2018-06-09',
    location: 'Palo Alto',
    name: 'Jet Isabella Mcnutt Deedee Bernstein',
    paid: 3456,
    percent: 40
  },
  {
    date: '2018-06-11',
    location: 'Boise',
    name: 'Michael Corazon Ragan September Hynes',
    paid: 1234,
    percent: 50
  },
  {
    date: '2018-06-10',
    location: 'San Francisco',
    name: 'Tracy Kimbery Mccrary Jona Kinsey',
    paid: 2345,
    percent: 10
  }
]

export const RolesAndQualificationsData: { [x: string]: string } = {
  Department: 'Surgery',
  'Educational Qualifications': 'MD',
  Role: 'Doctor'
}

export const EmploymentData: { [x: string]: string } = {
  'Employment Date': '06/11/2021',
  'Employment Number': 'AHDUILL0980'
}

export const DetailNote = {
  date: '11/12/23',
  name: 'Ajayi Festus',
  note: 'The X-Ray Machine has been malfunctioning for a while. I think a part of it not connected to the power sources '
}

export const searchHistory = [
  { name: 'John Doe', title: 'doctor' },
  { name: 'Damilola Ayegun', title: 'doctor' },
  { name: 'Femi Ogunshina', title: 'doctor' },
  { name: 'Folasade Adedokun', title: 'doctor' }
]

export const DetailAuditTrail = {
  date: 'Today at 11:00AM',

  getName: function (index: number): string {
    return this.names[index - 1]
  },
  getNote: function (index: number): string {
    return this.notes[index - 1]
  },
  names: [`Kudirat Amake`, `Dr. Joshua Adeyemi`, ``],

  notes: [` edited patients record: Address`, ` added to medical history: `, `Patient became an out-patient`],

  record: 'Address'
}

export const intaketTabs = ['Pending', 'Waiting', 'Completed']

export const intakeStatus = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Completed', value: 'completed' }
]

export const equipmentsData = [
  { category: 'Diagnostic', name: 'Ultra Sound Machine', status: '20/20', stockQuantity: 20 },
  { category: 'Diagnostic', name: 'CT Scanner', status: '5/5', stockQuantity: 5 },
  { category: 'Diagnostic', name: 'X-Ray Machine', status: '3/5', stockQuantity: 5 },
  { category: 'Traetment', name: 'Ventilators', status: '5/5', stockQuantity: 5 },
  { category: 'Diagnostic', name: 'Ventilators', status: '5/5', stockQuantity: 5 },
  { category: 'Diagnostic', name: 'Wheel Chair', status: '5/5', stockQuantity: 5 },
  { category: 'Laboratory', name: 'Vital Signs Machine', status: '5/5', stockQuantity: 5 },
  { category: 'Laboratory', name: 'Microscope', status: '5/5', stockQuantity: 5 },
  { category: 'Laboratory', name: 'Dialysis Machine', status: '5/11', stockQuantity: 5 },
  { category: 'Traetment', name: 'Dialysis Machine', status: '5/11', stockQuantity: 5 },
  { category: 'Traetment', name: 'Dialysis Machine', status: '5/11', stockQuantity: 5 },
  { category: 'Traetment', name: 'Dialysis Machine', status: '5/11', stockQuantity: 5 },
  { category: 'Traetment', name: 'Dialysis Machine', status: '5/11', stockQuantity: 5 },
  { category: 'Traetment', name: 'Dialysis Machine', status: '5/11', stockQuantity: 5 }
]

export const equipmentDetailList = [
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  },
  {
    condition: 'Excellent',
    department: 'Radiology',
    id: 'EQ001',
    name: 'X Ray Machine',
    next_maintenance_date: '06/11/2021',
    serial_number: '55559080050',
    status: 'Available'
  }
]

export const appointmentTypes = [
  { label: 'Consultation', value: 'consultation' },
  { label: 'Follow-up', value: 'follow-up' }
]
export const appointmentModes = ['Physical', 'Virtual'].map(label => ({
  ...createOption(label),
  isDisabled: label === 'Virtual'
}))

export const pageLimitOptions = [5, 10, 15, 20, 30, 50]

export const permissionRole = {
  Create: 'Can add to set entities on the dashboard',
  Edit: 'Can make edits to set entities on the dashboard',
  Manage: 'Can add to set entities on the dashboard',
  Read: 'Can add to set entities on the dashboard'
}

export const permissions = ['manage', 'create', 'edit', 'read']

export const supplierData = [
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  },
  {
    address: 'No.25 Ajayi Avenue, Ikeja. Lagos',
    contact_name: 'John Gbadebo',
    email: 'JohnGbadebo@gmail.com',
    name: 'Medpharma Cop',
    phone_number: '08169784011'
  }
]

export const supplierDetails = {
  'address 1': 'No.25 Ajayi Avenue, Ikeja. Lagos',
  'address 2': 'No.25 Ajayi Avenue, Ikeja. Lagos',
  'address 3': 'No.25 Ajayi Avenue, Ikeja. Lagos',
  'contact name': 'John Gbadebo',
  'email address': 'JohnGbadebo@gmail.com',
  'phone number': '08169784011',
  'supplier name': 'Medpharma Cop'
}

export const scheduleSort = ['all', 'on_call', 'morning', 'evening', 'night']

export const scheduleViews = ['week', 'day']

export const VIEW_OPTIONS = [
  { id: Views.DAY, label: 'Day' },
  { id: Views.WEEK, label: 'Week' }
]

export const localizer = dayjsLocalizer(dayjs)

export const lab_data = [
  {
    date: '2025-01-09',
    id: 1,
    interpretation: 'Normal',
    lab: 'Lab001',
    meta: { status: 'Completed' },
    test: 'Blood Test'
  },
  {
    date: '2025-01-08',
    id: 2,
    interpretation: 'Abnormal',
    lab: 'Lab002',
    meta: { status: 'Pending' },
    test: 'Urine Test'
  },

  {
    date: '2025-01-05',
    id: 5,
    interpretation: 'Negative',
    lab: 'Lab005',
    meta: { status: 'Completed' },
    test: 'COVID-19 PCR'
  }
]

export const imaging_data = [
  {
    date: 'Dr. Smith',
    id: 1,
    interpretation: '2023-10-01',
    meta: { status: 'Completed' },
    test: 'X-Ray'
  },
  {
    date: 'Dr. Johnson',
    id: 2,
    interpretation: '2023-10-02',
    meta: { status: 'Pending' },
    test: 'MRI'
  },
  {
    date: 'Dr. Brown',
    id: 3,
    interpretation: '2023-10-03',
    meta: { status: 'In Progress' },
    test: 'CT Scan'
  }
]

export const marStatus = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Adminstered', value: 'administered' },
  { label: 'Missed', value: 'missed' },
  { label: 'Refused', value: 'refused' },
  { label: 'Wasted', value: 'wasted' }
]

export const nurseMedicationStatus = [
  { label: 'All Status', value: '' },
  { label: 'Ongoing', value: 'ongoing' },
  { label: 'Completed', value: 'completed' },
  { label: 'Stopped', value: 'stopped' }
]

export const historyOptions = [
  { label: 'Medical', value: 'medical' },
  { label: 'Family', value: 'family' },
  { label: 'Surgical', value: 'surgical' },
  { label: 'Social', value: 'social' }
]

export const pharmacyMedStatus = [
  { label: 'All', value: 'all' },
  { label: 'Pending', value: 'pending' },
  { label: 'Under Review', value: 'review_requested' },
  { label: 'Verified', value: 'verified' }
]

export const bedStatus = [
  { label: 'All', value: 'all' },
  { label: 'Available', value: 'available' },
  { label: 'Not Available', value: 'not_in_service' }
]

export const bedRequestStatus = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Assigned', value: 'assigned' },
  { label: 'Rejected', value: 'rejected' }
]

export const billingStatus = [
  { label: 'All', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Paid', value: 'paid' },
  { label: 'Partially paid', value: 'partially_paid' }
]

export const bedType = ['Trauma Stretcher Beds', 'Transport Gurneys', 'Other']

export enum BedType {
  TraumaStretcherBeds = 'Trauma Stretcher Beds',
  TransportGurneys = 'Transport Gurneys',
  Other = 'Other'
}

export const encounterStatus = [
  { label: 'All', value: '' },
  { label: 'Waiting', value: 'pending' },
  { label: 'In Consultation', value: 'in_consultation' },
  { label: 'Completed', value: 'completed' }
]

export const statusOptions = [
  { label: 'All', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' }
]

export const consentForm = [
  {
    attributes: {
      name: 'medical consent form',
      updated: '06/11/2021'
    }
  },
  {
    attributes: {
      name: 'surgical consent form',
      updated: '06/11/2021'
    }
  },
  {
    attributes: {
      name: 'parent consent form',
      updated: '06/11/2021'
    }
  }
]

export const invoiceDetails = [
  {
    category: 'Room Charges',
    particular: 'Private Room',
    quantity: '3 Nights',
    totalCost: 'NGN 75,000',
    unitPrice: 'NGN 75,000'
  },
  {
    category: 'Room Charges',
    particular: 'Private Room',
    quantity: '3 Nights',
    totalCost: 'NGN 75,000',
    unitPrice: 'NGN 75,000'
  },
  {
    category: 'Room Charges',
    particular: 'Private Room',
    quantity: '3 Nights',
    totalCost: 'NGN 75,000',
    unitPrice: 'NGN 75,000'
  }
]

export const src = 'https://www.shareicon.net/data/512x512/2016/07/26/802033_user_512x512.png'

// Mock options for SelectInputField and other fields in AddStock.tsx

export const MANUFACTURER_OPTIONS = [
  { id: 'm1', name: 'Emzor Pharma' },
  { id: 'm2', name: 'GSK' },
  { id: 'm3', name: 'Pfizer' }
]

export const SUPPLIER_OPTIONS = [
  { id: 's1', name: 'Mega Distributors' },
  { id: 's2', name: 'HealthMart Supplies' },
  { id: 's3', name: 'Global Medics' }
]

export const LOCATION_OPTIONS = [
  { label: 'Main Pharmacy', value: 'pharmacy' },
  { label: 'Ward Store', value: 'ward' },
  { label: 'Emergency', value: 'emergency' }
]

export const VACCINE_VVM_STATUS_OPTIONS = [
  { label: 'Stage 1', value: 'vvm1' },
  { label: 'Stage 2', value: 'vvm2' },
  { label: 'Stage 3', value: 'vvm3' },
  { label: 'Stage 4', value: 'vvm4' }
]

export const UDDS_FLAT_PACKAGING = [
  'Blister Pack XL (1.04” x 0.62” x 0.67”)',
  'Blister Pack Square (1.01” x 0.87” x 0.87”)',
  'Blister Pack Large (1.64” x 0.7” x 0.87”)',
  'Blister Grid (Standard 5 x 5)',
  'Unit Dose Box (5” x 1.625” x 2.25”)',
  'Oral Liquid Cup (5 mL)',
  'Oral Liquid Cup (10 mL)',
  'Oral Liquid Cup (15 mL)',
  'Oral Liquid Cup (25 mL)',
  'Oral Liquid Cup (30 mL)',
  'Fluidose Cup (35 mL)',
  'Fluidose Cup (60 mL)',
  'Oral Syringe (1 mL)',
  'Oral Syringe (3 mL)',
  'Oral Syringe (5 mL)',
  'Oral Syringe (10 mL)',
  'Pre-filled Syringe (0.5 mL)',
  'Pre-filled Syringe (1 mL)',
  'Pre-filled Syringe (2.25 mL)',
  'Pre-filled Syringe (3 mL)',
  'Pre-filled Syringe (5 mL)',
  'High-Volume Syringe (10 mL)',
  'High-Volume Syringe (20 mL)',
  'Insulin Syringe (0.3 mL / 30 Units)',
  'Insulin Syringe (0.5 mL / 50 Units)',
  'Insulin Syringe (1 mL / 100 Units)',
  'Pharma Stick Pack (80-100 mm x 20 mm)',
  'Pharma Stick Pack (50-90 mm x 15 mm)',
  'Smart Pack Pouch (2.0” x 1.10”)',
  'Smart Pack Pouch (2.0” x 1.32”)',
  'Smart Pack Pouch (2.0” x 1.65”)',
  'Smart Pack Pouch (2.0” x 2.2”)',
  'Ointment Blister Small (1.2 mL)',
  'Ointment Blister Large (1.8 mL)',
  'Collapsible Tube (1 g)',
  'Collapsible Tube (2 g)',
  'Collapsible Tube (5 g)'
]

export const tabOptions = [
  { key: 'all', label: 'All' },
  { key: 'in_stock', label: 'In Stock' },
  { key: 'out_of_stock', label: 'Out of Stock' },
  { key: 'expiring', label: 'Expiring' },
  { key: 'expired', label: 'Expired' }
]

export const UoM = [
  'mg',
  'g',
  'mcg',
  'µg',
  'mg/mL',
  'U',
  'IU',
  'mmol/L',
  'mEq/L',
  'ng/mL',
  'µg/L',
  'pg/mL',
  '% w/w',
  '% w/v',
  '% v/v',
  'U/kg',
  'IU/kg',
  'mg/kg',
  'mcg/kg',
  'µg/kg',
  'mEq/mL',
  'µg/mL',
  'ng/L',
  'pmol/L',
  'µmol/L',
  'mL',
  'L',
  'ng',
  'µg/dL',
  'mg/dL',
  'g/dL',
  'mIU/mL',
  'kU/L',
  'U/L',
  'IU/L',
  'U/g',
  'IU/g',
  'AU',
  'MU',
  'M',
  'mM',
  'µM',
  'nM',
  'ppm',
  'ppb'
]

// src/utils/helper.ts

export interface BulkField {
  label: string
  name: string
}

export const STAFF_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'First Name', name: 'first_name' },
  { label: 'Last Name', name: 'last_name' },
  { label: 'Other Name', name: 'middle_name' },
  { label: 'Email', name: 'email' },
  { label: 'Phone Number', name: 'phone_number' },
  { label: 'Gender', name: 'gender' },
  { label: 'Date Of Birth', name: 'date_of_birth' },
  { label: 'Department', name: 'department' },
  { label: 'Specialty', name: 'specialty' },
  { label: 'Role', name: 'role' },
  { label: 'Facility', name: 'facility' }
]

export const HMO_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'HMO Name', name: 'hmo_name' },
  { label: 'Plan Name', name: 'plan_name' },
  { label: 'Service Name', name: 'service_name' },
  { label: 'Price', name: 'price' },
  { label: 'Service Type', name: 'service_type' }
]

export const PATIENT_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'First Name', name: 'first_name' },
  { label: 'Middle Name', name: 'middle_name' },
  { label: 'Last Name', name: 'last_name' },
  { label: 'Gender', name: 'gender' },
  { label: 'Email', name: 'email' },
  { label: 'Date Of Birth', name: 'date_of_birth' },
  { label: 'Phone Number', name: 'phone_number' },
  { label: 'Hospital number', name: 'ref' }
]

export const PROCEDURE_ORDER_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'Procedure', name: 'procedure' },
  { label: 'Note (optional)', name: 'notes' }
]

export const PROCEDURE_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'Name', name: 'name' },
  { label: 'Price', name: 'price' },
  { label: 'Description', name: 'description' }
]

export const UNIT_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'Name', name: 'name' },
  { label: 'Code', name: 'code' },
  { label: 'Department', name: 'department' },
  { label: 'Description', name: 'description' }
]

/** Aligned with Workspace Create Product / Add Stock (bulk API accepts purchase_qty). */
export const PRODUCT_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'Inventory Location', name: 'inventory_location' },
  { label: 'Product name', name: 'generic_name' },
  { label: 'Brand name', name: 'brand_name' },
  { label: 'Form / kind', name: 'dosage_form' },
  { label: 'Route', name: 'route' },
  { label: 'Base UoM', name: 'inventory_unit' },
  { label: 'Requires Rx', name: 'requires_rx' },
  { label: 'OTC', name: 'otc' },
  { label: 'Therapeutic category', name: 'therapeutic_category' },
  { label: 'Strength', name: 'strength' },
  { label: 'Reorder level', name: 'reorder_level' },
  { label: 'Batch number', name: 'batch_number' },
  { label: 'Expiry date', name: 'expiry_date' },
  { label: 'Manufacture date', name: 'date_of_manufacture' },
  { label: 'Purchase qty', name: 'purchase_qty' },
  { label: 'Units per purchase unit', name: 'pack_quantity' },
  { label: 'Purchase unit', name: 'packaging' },
  { label: 'NAFDAC', name: 'nafdac_number' },
  { label: 'Unit cost', name: 'unit_cost' },
  { label: 'Selling price', name: 'selling_price' },
  { label: 'Stock Location', name: 'stock_location' }
]

export const IMAGING_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'Category', name: 'category' },
  { label: 'Price', name: 'price' },
  { label: 'Name', name: 'name' },
  { label: 'Study Type', name: 'study_type' },
  { label: 'Description (Optional)', name: 'description' },
  { label: 'Default (Optional)', name: 'default' }
]

export const LAB_TEST_BULK_UPLOAD_FIELDS: BulkField[] = [
  { label: 'Name', name: 'name' },
  { label: 'Category', name: 'category' },
  { label: 'Price', name: 'price' },
  { label: 'Description (Optional)', name: 'description' },
  { label: 'Default (Optional)', name: 'default' }
]
